import {
  formatRecipientLabel,
  normalizeRecipientOption,
  uniqueRecipientsByKey,
} from '@/composables/people/recipientOptions';
import collaboratorsService from '@/services/collaborators';
import invoicesService from '@/services/invoices';
import peopleService from '@/services/people';
import { SEND_EMAIL_TEXT } from '@/texts/catalog/sendEmail';
import { collectArrayCandidates } from '@/utils/collectionValue';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { formatCurrency } from '@/utils/numberFormat';
import { resolveText } from '@/utils/textValue';

const resolveInvoiceText = () => {
  const section = SEND_EMAIL_TEXT?.invoices;
  return section && typeof section === 'object' ? section : {};
};

const uniqueById = (items = []) => {
  const seen = new Set();
  const uniqueItems = [];

  (Array.isArray(items) ? items : []).forEach((item) => {
    const id = resolveText(item?.id || '', '');
    if (id === '' || seen.has(id)) {
      return;
    }

    seen.add(id);
    uniqueItems.push(item);
  });

  return uniqueItems;
};

const normalizeInvoiceAttachmentOption = (value, index = 0) => {
  if (!value || typeof value !== 'object') {
    const label = resolveText(value, '');
    if (label === '') {
      return null;
    }

    return {
      id: `invoice-${index}-${label}`,
      label,
      folio: '',
      date: '',
      totalLabel: '',
      uuid: '',
    };
  }

  const id = resolveText(value.id ?? value.value ?? value.invoice_id ?? '', `invoice-${index}`);
  const folio = resolveText(value.folio ?? value.serial_folio ?? value.label ?? '', '');
  const date = resolveText(value.date ?? '', '');
  const totalLabel = resolveText(value.totalLabel ?? value.total_label ?? value.total ?? '', '');
  const uuid = resolveText(value.uuid ?? '', '');
  const label = folio !== '' ? folio : resolveText(value.label ?? value.name ?? value.title ?? '', '');

  if (label === '' && date === '' && totalLabel === '' && uuid === '') {
    return null;
  }

  return {
    id,
    label: label !== '' ? label : `Factura ${index + 1}`,
    folio,
    date,
    totalLabel,
    uuid,
  };
};

const formatCustomerPlaceholderName = (value = '') => {
  const normalized = resolveText(value, '').replace(/\s+/g, ' ').trim();
  if (normalized === '') {
    return '';
  }

  const lower = normalized.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const normalizeRecipientsCollection = (collection = [], prefix = 'recipient') => {
  const normalized = (Array.isArray(collection) ? collection : [])
    .map((item, index) => normalizeRecipientOption(item, index, prefix))
    .filter(Boolean);

  return uniqueRecipientsByKey(normalized);
};

const resolveInvoiceContextData = (vm) => {
  const contextData = vm.contextData && typeof vm.contextData === 'object' ? vm.contextData : {};
  const invoiceData = contextData.invoiceData && typeof contextData.invoiceData === 'object'
    ? contextData.invoiceData
    : {};

  const selectedReceiver = contextData.selectedReceiver && typeof contextData.selectedReceiver === 'object'
    ? contextData.selectedReceiver
    : (invoiceData.selectedReceiver && typeof invoiceData.selectedReceiver === 'object' ? invoiceData.selectedReceiver : {});

  const invoiceId = Number(contextData.id || contextData.invoiceId || invoiceData.id || 0) || 0;
  const serial = resolveText(invoiceData.serial, '');
  const folio = resolveText(invoiceData.folio, '');
  const invoiceReference = `${serial}${folio}`.trim();

  const customerName = resolveText(
    contextData.customerName
      || invoiceData.customer_name
      || invoiceData.customerName
      || invoiceData.customer
      || selectedReceiver?.meta?.clientName
      || selectedReceiver?.clientName
      || selectedReceiver?.meta?.name
      || selectedReceiver?.name
      || selectedReceiver?.label
      || selectedReceiver?.meta?.businessName
      || selectedReceiver?.businessName,
    ''
  );

  const total = Number(contextData.total ?? invoiceData.total ?? 0) || 0;
  const currency = resolveText(contextData.currency || invoiceData.currency, '');

  return {
    invoiceId,
    invoiceData,
    selectedReceiver,
    invoiceReference,
    customerName,
    total,
    currency,
  };
};

const resolveInvoiceOwnerId = (invoiceContext = {}) => {
  const invoiceData = invoiceContext.invoiceData && typeof invoiceContext.invoiceData === 'object'
    ? invoiceContext.invoiceData
    : {};
  const selectedReceiver = invoiceContext.selectedReceiver && typeof invoiceContext.selectedReceiver === 'object'
    ? invoiceContext.selectedReceiver
    : {};

  const invoiceCustomerId = Number(invoiceData?.customer_id || invoiceData?.company_id || 0) || 0;
  if (invoiceCustomerId > 0) {
    return invoiceCustomerId;
  }

  const receiverMeta = selectedReceiver?.meta || {};

  return Number(
    receiverMeta?.customer_id
      || receiverMeta?.company_id
      || selectedReceiver?.customer_id
      || selectedReceiver?.company_id
      || selectedReceiver?.value
      || selectedReceiver?.id
      || receiverMeta?.id
      || 0
  ) || 0;
};

const resolveInvoicePrimaryRecipient = (invoiceContext = {}) => {
  const invoiceData = invoiceContext.invoiceData && typeof invoiceContext.invoiceData === 'object'
    ? invoiceContext.invoiceData
    : {};
  const selectedReceiver = invoiceContext.selectedReceiver && typeof invoiceContext.selectedReceiver === 'object'
    ? invoiceContext.selectedReceiver
    : {};
  const receiverMeta = selectedReceiver?.meta || {};

  const name = resolveText(
    selectedReceiver?.label
      || receiverMeta?.clientName
      || receiverMeta?.businessName
      || invoiceContext.customerName,
    ''
  );

  const email = resolveText(
    selectedReceiver?.email
      || receiverMeta?.email
      || invoiceData?.customer_email
      || invoiceData?.email,
    ''
  );

  return {
    id: resolveText(selectedReceiver?.value || receiverMeta?.id || '', ''),
    name,
    email,
    label: formatRecipientLabel(name, email),
  };
};

const resolveInvoiceFallbackRecipients = (invoiceContext = {}, primaryRecipient = {}) => {
  const candidates = [];

  if (primaryRecipient && typeof primaryRecipient === 'object') {
    candidates.push(primaryRecipient);
  }

  const invoiceData = invoiceContext.invoiceData && typeof invoiceContext.invoiceData === 'object'
    ? invoiceContext.invoiceData
    : {};

  [
    'persons',
    'people',
    'contacts',
    'contact_people',
    'copyRecipients',
    'ccRecipients',
    'sendCcOptions',
  ].forEach((key) => {
    const list = Array.isArray(invoiceData[key]) ? invoiceData[key] : [];
    list.forEach((item) => {
      candidates.push(item);
    });
  });

  return normalizeRecipientsCollection(candidates, 'fallback-recipient');
};

const buildInvoiceSummaryMeta = (invoiceContext = {}) => {
  const summaryText = resolveInvoiceText()?.summary || {};
  const sendTypeLabelValue = resolveText(summaryText?.sendTypeValue, '');
  const reference = resolveText(invoiceContext.invoiceReference, '');
  const sendTypeValue = `${sendTypeLabelValue}${reference !== '' ? ` ${reference}` : ''}`.trim();

  return {
    sendTypeLabel: resolveText(summaryText?.sendTypeLabel, ''),
    sendTypeValue,
    counterpartLabel: resolveText(summaryText?.customerLabel || summaryText?.counterpartLabel, ''),
    counterpartValue: resolveText(invoiceContext.customerName, ''),
    totalLabel: resolveText(summaryText?.totalLabel, ''),
    totalValue: formatCurrency(invoiceContext.total, { currency: invoiceContext.currency || 'MXN' }),
  };
};

const resolveInvoiceInitialToRecipientIds = (vm) => {
  const options = Array.isArray(vm.normalizedToRecipientOptions) ? vm.normalizedToRecipientOptions : [];

  const preferredIds = options
    .filter((item) => Number(item?.invoice_mailing || 0) === 1 && resolveText(item?.email, '') !== '')
    .map((item) => item.id);

  if (preferredIds.length > 0) {
    return preferredIds;
  }

  const primaryId = resolveText(vm.normalizedPrimaryRecipient?.id, '');
  if (primaryId !== '') {
    return [primaryId];
  }

  const firstRecipient = options.find((item) => resolveText(item?.email, '') !== '');
  return firstRecipient ? [firstRecipient.id] : [];
};

const resolveInvoiceInitialCcRecipientIds = (vm) => {
  const options = Array.isArray(vm.normalizedCcRecipientOptions) ? vm.normalizedCcRecipientOptions : [];

  return options
    .filter((item) => Number(item?.invoice_mailing || 0) === 1 && resolveText(item?.email, '') !== '')
    .map((item) => item.id);
};

const resolveInvoiceDefaultMessage = (customerName = '') => {
  const defaultMessage = resolveInvoiceText()?.defaultMessage;
  if (typeof defaultMessage === 'function') {
    return String(defaultMessage(customerName || '') || '');
  }

  return resolveText(defaultMessage, '');
};

const hydrateInvoiceContextData = async (vm) => {
  const invoiceContext = resolveInvoiceContextData(vm);
  const ownerId = resolveInvoiceOwnerId(invoiceContext);
  const primaryRecipient = resolveInvoicePrimaryRecipient(invoiceContext);

  vm.text = resolveInvoiceText();
  vm.summaryMeta = buildInvoiceSummaryMeta(invoiceContext);
  vm.primaryRecipient = primaryRecipient;
  vm.messageType = 'invoice';

  const customerName = formatCustomerPlaceholderName(invoiceContext.customerName);
  vm.messageVariables = {
    customer: customerName,
    customerName,
    NOMBRE_CLIENTE: customerName,
  };
  vm.messageLoadingText = resolveText(vm.text?.messageLoadingText, '');
  vm.initialMessage = resolveInvoiceDefaultMessage(customerName);
  vm.recipientsLoading = ownerId > 0;

  const [recipientsResult, copyResult, additionalInvoicesResult, documentsResult] = await Promise.allSettled([
    ownerId > 0 ? peopleService.selectors({ customer_id: ownerId }) : Promise.resolve([]),
    collaboratorsService.invoiceMailingSelectors(),
    ownerId > 0
      ? invoicesService.additionalSelectors({
        customer_id: ownerId,
        exclude_invoice_id: invoiceContext.invoiceId,
      })
      : Promise.resolve([]),
    invoiceContext.invoiceId > 0
      ? invoicesService.documentsIndex(invoiceContext.invoiceId)
      : Promise.resolve({}),
  ]);

  let recipientOptions = [];
  if (recipientsResult.status === 'fulfilled') {
    recipientOptions = normalizeRecipientsCollection(extractCollection(recipientsResult.value), 'person');
  }

  if (recipientOptions.length === 0) {
    recipientOptions = resolveInvoiceFallbackRecipients(invoiceContext, primaryRecipient);
  }

  let copyOptions = [];
  if (copyResult.status === 'fulfilled') {
    copyOptions = normalizeRecipientsCollection(extractCollection(copyResult.value), 'collaborator');
  }

  let additionalInvoiceOptions = [];
  if (additionalInvoicesResult.status === 'fulfilled') {
    additionalInvoiceOptions = extractCollection(additionalInvoicesResult.value);
  }

  const invoicePayloadAttachmentOptions = collectArrayCandidates(invoiceContext.invoiceData, [
    'additionalInvoices',
    'relatedInvoices',
    'attachableInvoices',
    'invoiceAttachmentOptions',
  ]);

  let serviceDocuments = [];
  if (documentsResult.status === 'fulfilled') {
    const payload = extractPayload(documentsResult.value);
    serviceDocuments = Array.isArray(payload?.documents) ? payload.documents : [];
  }

  const invoicePayloadDocumentOptions = collectArrayCandidates(invoiceContext.invoiceData, [
    'documentAttachmentOptions',
    'documentsOptions',
    'filesOptions',
    'attachmentsDocuments',
  ]);

  vm.recipientOptions = recipientOptions;
  vm.copyOptions = copyOptions;
  vm.invoiceAttachmentOptions = uniqueById(
    [...additionalInvoiceOptions, ...invoicePayloadAttachmentOptions]
      .map((item, index) => normalizeInvoiceAttachmentOption(item, index))
      .filter(Boolean)
  );
  vm.documentAttachmentOptions = uniqueById([
    ...serviceDocuments,
    ...invoicePayloadDocumentOptions,
  ]);

  vm.recipientsLoading = false;
  vm.initialToRecipientIds = resolveInvoiceInitialToRecipientIds(vm);
  vm.initialCcRecipientIds = resolveInvoiceInitialCcRecipientIds(vm);
};

const resolveInvoiceAttachmentSummaryLabel = (vm, option) => {
  const folio = resolveText(option?.folio || option?.label || '', '');
  if (folio !== '') {
    return `${folio} (PDF/XML)`;
  }

  const fallbackLabel = resolveText(vm.resolvedText?.summary?.sendTypeValue, '');
  const label = resolveText(option?.label || '', fallbackLabel);
  return label !== '' ? `${label} (PDF/XML)` : 'PDF/XML';
};

const resolveSelectedInvoiceAttachmentItems = (vm) => {
  return (Array.isArray(vm.selectedInvoiceAttachments) ? vm.selectedInvoiceAttachments : [])
    .map((option) => ({
      ...option,
      label: resolveInvoiceAttachmentSummaryLabel(vm, option),
      group: 'invoice',
    }));
};

const resolveDefaultInvoiceAttachmentItem = (vm) => {
  const fallbackReference = resolveText(
    vm.resolvedText?.summary?.sendTypeValue,
    resolveText(vm.summaryData?.sendTypeValue, '')
  );
  const invoiceReference = resolveText(vm.summaryData?.sendTypeValue, fallbackReference);
  const label = invoiceReference !== ''
    ? `${invoiceReference} (PDF/XML)`
    : 'PDF/XML';

  return {
    id: 'invoice-default-pdf-xml',
    label,
    group: 'invoice-default',
    locked: true,
  };
};

export const sendEmailInvoiceContextConfig = Object.freeze({
  supportsInvoiceAttachments: true,
  resolveText() {
    return resolveInvoiceText();
  },
  hydrateContextData: hydrateInvoiceContextData,
  resolveSelectedInvoiceAttachmentItems,
  resolveDefaultInvoiceAttachmentItem,
  resolveInvoiceAttachmentSummaryLabel,
});
